/**
 * wishlist-manager.js
 * ---------------------------------------------------------
 * Persists wishlist in localStorage (survives page refresh & tab close).
 * Key: 'shopify_wishlist'  →  JSON array of product IDs (strings)
 *
 * Public API (window.Wishlist):
 *   Wishlist.getAll()              → string[]
 *   Wishlist.has(productId)        → boolean
 *   Wishlist.add(productId)        → void
 *   Wishlist.remove(productId)     → void
 *   Wishlist.toggle(productId)     → boolean  (true = now in wishlist)
 *   Wishlist.count()               → number
 *
 * DOM events fired on document:
 *   'wishlist:change'  →  detail: { productId, inWishlist, all }
 * ---------------------------------------------------------
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'shopify_wishlist';

  /* ---------- storage helpers ---------- */

  function load() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch (_) {
      return [];
    }
  }

  function save(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (_) {}
  }

  function emit(productId, inWishlist, all) {
    document.dispatchEvent(
      new CustomEvent('wishlist:change', {
        bubbles: true,
        detail: { productId: String(productId), inWishlist: inWishlist, all: all },
      })
    );
  }

  /* ---------- public API ---------- */

  var Wishlist = {
    getAll: function () {
      return load();
    },
    has: function (productId) {
      return load().indexOf(String(productId)) !== -1;
    },
    add: function (productId) {
      var id = String(productId);
      var list = load();
      if (list.indexOf(id) === -1) {
        list.push(id);
        save(list);
        emit(id, true, list);
        updateCountBadges(list.length);
      }
    },
    remove: function (productId) {
      var id = String(productId);
      var list = load().filter(function (i) { return i !== id; });
      save(list);
      emit(id, false, list);
      updateCountBadges(list.length);
    },
    toggle: function (productId) {
      if (Wishlist.has(productId)) {
        Wishlist.remove(productId);
        return false;
      } else {
        Wishlist.add(productId);
        return true;
      }
    },
    count: function () {
      return load().length;
    },
  };

  window.Wishlist = Wishlist;

  /* ---------- count badge ---------- */

  function updateCountBadges(count) {
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  /* ---------- button active state sync ---------- */

  function syncButton(btn) {
    var productId = btn.dataset.productId;
    if (!productId) return;
    var active = Wishlist.has(productId);
    btn.classList.toggle('wishlist-active', active);
    btn.setAttribute('aria-pressed', String(active));
    btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
  }

  function syncAllButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(syncButton);
  }

  /* ---------- click delegation (only for [data-wishlist-btn] toggle buttons) ---------- */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wishlist-btn]');
    if (!btn) return;
    e.preventDefault();
    var productId = btn.dataset.productId;
    if (!productId) return;
    Wishlist.toggle(productId);
  });

  /* ---------- respond to wishlist changes ---------- */

  document.addEventListener('wishlist:change', function (e) {
    /* Sync any [data-wishlist-btn] toggle buttons for this product */
    var productId = e.detail.productId;
    document.querySelectorAll('[data-wishlist-btn][data-product-id="' + productId + '"]').forEach(syncButton);

    /* Remove the wishlist-page list row if un-wishlisted */
    if (!e.detail.inWishlist) {
      var card = document.querySelector(
        '.wishlist-list [data-wishlist-card="' + productId + '"]'
      );
      if (card) {
        card.classList.add('removing');
        setTimeout(function () {
          card.remove();
          checkEmpty();
        }, 300);
      }
    }
  });

  function checkEmpty() {
    var list = document.querySelector('.wishlist-list');
    if (!list) return;
    if (!list.querySelector('[data-wishlist-card]')) {
      var empty = document.querySelector('.wishlist-empty-state');
      if (empty) empty.style.display = '';
      list.style.display = 'none';

      /* Update subtitle to "No items saved" */
      var subtitle = document.getElementById('wishlist-item-count');
      if (subtitle) subtitle.textContent = 'No items saved';
    }
  }

  /* ---------- init on DOM ready ---------- */

  function init() {
    syncAllButtons();
    updateCountBadges(Wishlist.count());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();